import b from 'b_';
import cx from 'classnames';
import React, { useState, useRef, useEffect } from 'react';

import { SimpleCallback } from '../../typings';
import { KEYBOARD_TYPES } from '../keyboard/types';
import UseKeyboard from '../keyboard/use-keyboard';

import PaletteButton from './__button/palette__button';
import PaletteTooltip from './__tooltip/palette__tooltip';
import { PaletteTool, PALETTE_ORIENTATION } from './types';
import { getTooltipDirection } from './utils';

import './palette.scss';

export function isSameTool(color1?: PaletteTool, color2?: PaletteTool): boolean {
    return Boolean(color1 && color2 && color1.id === color2.id);
}

interface Props<T extends PaletteTool> {
    className?: string;
    tool?: T;
    hideTooltip?: SimpleCallback;
    onChange: (toolId?: T['id']) => void;
    palette: T[];
    readOnly: boolean;
    shouldShowTooltip?: boolean;
    setHoveredTool?: (toolId?: string) => void;
    tooltip?: React.ReactChild;
    orientation?: PALETTE_ORIENTATION;
    keyboardType?: KEYBOARD_TYPES | null;
}

const Palette = <T extends PaletteTool>(props: Props<T>) => {
    const {
        tool,
        hideTooltip,
        onChange,
        palette,
        readOnly,
        shouldShowTooltip,
        setHoveredTool,
        tooltip,
        orientation = PALETTE_ORIENTATION.HORIZONTAL,
        keyboardType,
    } = props;

    const tooltipTargetRef = useRef<HTMLDivElement>(null);
    const [hoveredToolButton, setHoveredToolButton] = useState<PaletteTool>();

    useEffect(() => {
        if (readOnly) {
            onChange(undefined);
        }
    }, [onChange, readOnly]);

    const getPaletteButtonProps = (paletteTool: T) => ({
        orientation,
        popupLabel: paletteTool.label,
        hovered: isSameTool(paletteTool, hoveredToolButton),
        onMouseEnter: () => {
            if (setHoveredTool) {
                setHoveredTool(paletteTool.id);
            }
            setHoveredToolButton(paletteTool);
        },
        onMouseLeave: () => {
            if (setHoveredTool) {
                setHoveredTool(undefined);
            }
            setHoveredToolButton(undefined);
        },
        onClick: () => {
            if (!isSameTool(paletteTool, tool)) {
                onChange(paletteTool.id);
            }
        },
    });

    const PaletteWithoutKeyboard = (
        <div className={cx('palette__wrapper', props.className)}>
            <div
                className={b('palette', { readonly: readOnly, orientation })}
                onMouseEnter={hideTooltip}
                ref={tooltipTargetRef}
            >
                {palette.map((paletteTool) => (
                    <PaletteButton key={paletteTool.id} {...getPaletteButtonProps(paletteTool)}>
                        {paletteTool.children}
                    </PaletteButton>
                ))}
            </div>
            <PaletteTooltip
                delayShow={0}
                direction={getTooltipDirection(orientation)}
                show={Boolean(shouldShowTooltip)}
                target={tooltipTargetRef}
            >
                {tooltip}
            </PaletteTooltip>
        </div>
    );

    if (!keyboardType) {
        return PaletteWithoutKeyboard;
    }

    return (
        <UseKeyboard keyboardType={keyboardType} show={true}>
            {PaletteWithoutKeyboard}
        </UseKeyboard>
    );
};

export default Palette;
